// src/pages/PrivacyPolicy.js
import React from "react";
import "./TermsAndPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="terms-and-policy-container">
      <h1>Andeon Apps Privacy Policy</h1>
      <p>Last Updated: May 25, 2026</p>

      <h2>1. Introduction</h2>
      <p>
        Your privacy is important to us. It is Andeon Apps' policy to respect
        your privacy regarding any information we may collect from you through
        Andeon Apps (the "Service"), an AI-powered tool that turns still
        images into short videos. This policy explains what information we collect,
        how we use it, and the choices you have. The Service is offered only
        in the United States; users from outside the United States are not
        permitted to use the Service.
      </p>

      <h2>2. Information We Collect</h2>
      <p>
        We only ask for personal information when we truly need it to provide
        the Service. We collect it by fair and lawful means, with your
        knowledge and consent. The information we collect includes:
      </p>
      <ul>
        <li>
          <strong>Account information</strong> — name, email address, and any
          profile information you provide when you sign up. If you sign in
          with GitHub, we receive your GitHub user ID, username, primary
          email, avatar URL, and the public profile fields associated with
          the OAuth scopes <code>read:user</code> and <code>user:email</code>.
          We do not request access to your repositories or any private
          GitHub data.
        </li>
        <li>
          <strong>Uploaded content</strong> — the images you upload and the
          text prompts you submit so that we can generate videos for you.
          Images may contain faces or other personally identifiable
          features; you are responsible for obtaining any consent required
          from individuals depicted in your uploads, and you must not
          upload images intended to identify or biometrically profile any
          individual.
        </li>
        <li>
          <strong>Generated content</strong> — the videos produced by the
          Service and metadata about each generation job (timestamps,
          status, duration, settings).
        </li>
        <li>
          <strong>Billing information</strong> — if you purchase a
          subscription or credits, our payment processor (Stripe) collects
          your payment details directly. We do not store full card numbers
          on our servers; we retain only a customer identifier, plan, and
          transaction history.
        </li>
        <li>
          <strong>Usage and device information</strong> — basic technical
          information such as IP address, browser type, device type, and
          interactions with the Service, used for security, debugging, and
          analytics.
        </li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, operate, and maintain the Service;</li>
        <li>
          Process the images and prompts you submit in order to generate
          videos and deliver them back to you;
        </li>
        <li>Manage your account, subscription, and credit balance;</li>
        <li>Process payments and prevent fraudulent transactions;</li>
        <li>
          Communicate with you about your account, billing, service
          updates, and support requests;
        </li>
        <li>Improve, troubleshoot, and develop new features for the Service;</li>
        <li>Comply with legal obligations and enforce our Terms of Service.</li>
      </ul>
      <p>
        Andeon Apps does not use your User Content (uploaded images,
        prompts, or generated videos) to train artificial intelligence or
        machine learning models.
      </p>

      <h2>4. AI Processing and Third-Party Sub-processors</h2>
      <p>
        To generate videos, we send the images and prompts you submit to
        third-party AI providers (currently xAI). Under xAI's API terms
        in effect as of the date above, content submitted through the API
        is not used to train xAI's models by default. xAI processes
        submissions under its own terms in order to return a generated
        video to us, which we then deliver to you. We use the following
        categories of sub-processors:
      </p>
      <ul>
        <li>
          <strong>AI model providers</strong> (xAI) — to generate videos
          from your uploads and prompts.
        </li>
        <li>
          <strong>Cloud infrastructure and storage</strong> (Supabase,
          Vercel, Cloudflare) — to host the application, authenticate
          users, and store uploaded images and generated videos.
        </li>
        <li>
          <strong>Payment processing</strong> (Stripe) — to handle
          subscriptions, one-time purchases, and credit transactions.
        </li>
        <li>
          <strong>Error monitoring</strong> (Sentry, when enabled) — to
          detect and diagnose application errors.
        </li>
      </ul>

      <h2>5. Sharing and Disclosure</h2>
      <p>We do not sell your personal information. We may share it:</p>
      <ul>
        <li>
          With the sub-processors described in Section 4, solely to
          operate the Service;
        </li>
        <li>
          When required by law, court order, or governmental request, or
          to investigate suspected fraud or violations of our Terms;
        </li>
        <li>
          To protect the rights, property, or safety of Andeon Apps, our
          users, or the public;
        </li>
        <li>
          In connection with a merger, acquisition, financing, or sale of
          assets, in which case we will require the receiving party to
          honor this policy.
        </li>
      </ul>

      <h2>6. Data Retention and Deletion</h2>
      <p>
        We retain your account information, uploaded images, generated
        videos, and billing records for as long as your account is active
        or as needed to provide the Service. You may delete individual
        uploads or generated videos from your dashboard at any time;
        deleted items are removed from active systems within thirty (30)
        days and purged from routine encrypted backups within ninety (90)
        days. If you delete your account, we will delete or anonymize your
        personal data on the same timeline, except where we are required
        to retain it for legal, tax, accounting, or fraud-prevention
        purposes.
      </p>

      <h2>7. Data Security and Breach Notification</h2>
      <p>
        We value your trust in providing us your personal information and
        use commercially reasonable measures to protect it, including
        encrypted transport (HTTPS), authenticated storage, and access
        controls. No method of transmission over the internet or method
        of electronic storage is 100% secure, however, and we cannot
        guarantee its absolute security. In the event of a data breach
        affecting your personal information, we will notify you and any
        applicable regulators without undue delay, as required by
        applicable law.
      </p>

      <h2>8. Cookies and Similar Technologies</h2>
      <p>
        We and our sub-processors use cookies and similar local-storage
        technologies to keep you signed in, remember your preferences,
        and operate the Service. These include:
      </p>
      <ul>
        <li>
          <strong>Authentication cookies</strong> set by Supabase to
          maintain your signed-in session;
        </li>
        <li>
          <strong>OAuth state cookies</strong> used during GitHub sign-in
          to protect against cross-site request forgery;
        </li>
        <li>
          <strong>Preference storage</strong> in your browser's local
          storage (for example, theme selection);
        </li>
        <li>
          <strong>Payment cookies</strong> set by Stripe on its checkout
          pages, governed by Stripe's privacy policy.
        </li>
      </ul>
      <p>
        We do not use third-party advertising cookies or cross-site
        tracking pixels. You can disable or clear cookies through your
        browser settings, but doing so may prevent you from signing in or
        using parts of the Service.
      </p>

      <h2>9. Geographic Scope</h2>
      <p>
        The Service is intended for users in the United States only. Our
        infrastructure and sub-processors store and process your
        information in the United States. If you access the Service from
        outside the United States, you do so without authorization and
        at your own risk; we do not undertake to comply with the data
        protection laws of any non-U.S. jurisdiction.
      </p>

      <h2>10. Your Data Protection Rights</h2>
      <p>
        Subject to applicable law, you may have the right to access,
        correct, export, or delete the personal information we hold about
        you, and to object to or restrict certain processing. You can
        manage and delete most of your account data directly within the
        Service. For any request you cannot complete yourself, contact
        us at the address in Section 14 and we will respond within
        forty-five (45) days, with one possible extension as permitted by
        law. We will not discriminate against you for exercising these
        rights.
      </p>

      <h2>11. California and Other U.S. State Privacy Rights</h2>
      <p>
        If you are a California resident, the California Consumer Privacy
        Act, as amended by the California Privacy Rights Act (collectively,
        the "CCPA"), gives you the following rights:
      </p>
      <ul>
        <li>
          <strong>Right to know</strong> the categories and specific
          pieces of personal information we have collected about you, the
          sources, the business purposes for collection, and the
          categories of third parties with whom we share it (described
          throughout this policy);
        </li>
        <li>
          <strong>Right to delete</strong> personal information we have
          collected from you;
        </li>
        <li>
          <strong>Right to correct</strong> inaccurate personal
          information;
        </li>
        <li>
          <strong>Right to limit use of sensitive personal information</strong>
          (Andeon Apps does not use sensitive personal information for any
          purpose beyond what is necessary to provide the Service);
        </li>
        <li>
          <strong>Right to opt out of sale or sharing</strong> for
          cross-context behavioral advertising (we do not sell or share
          personal information for these purposes);
        </li>
        <li>
          <strong>Right to non-discrimination</strong> for exercising
          your rights;
        </li>
        <li>
          <strong>Right to use an authorized agent</strong> to submit
          requests on your behalf, subject to verification.
        </li>
      </ul>
      <p>
        Residents of Virginia, Colorado, Connecticut, Utah, Texas,
        Oregon, and other states with comprehensive consumer privacy
        laws may have similar rights, including the right to access,
        delete, correct, and obtain a portable copy of their personal
        information, and to opt out of targeted advertising, sale, and
        certain profiling. To exercise any of these rights, email us at
        the address in Section 14.
      </p>

      <h2>12. Children's Privacy</h2>
      <p>
        The Service is not offered to anyone under the age of 18, and
        we do not knowingly collect personal information from children.
        See Section 2 of our Terms of Service for our eligibility
        requirements. If you are a parent or guardian and believe your
        child has provided us with personal information, please contact
        us so we can take appropriate action, including deleting the
        information and closing the account.
      </p>

      <h2>13. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will
        notify you of material changes by posting the updated policy on
        this page and updating the "Last Updated" date above. Your
        continued use of the Service after changes become effective
        constitutes acceptance of the revised policy.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy or our data
        practices, please contact us at legal@andeonapps.com.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
